/**
 * Verified Cross Post — canonical continuity in XO, representations elsewhere.
 * Local-first demo fallback when sign-post API is unavailable.
 */

import { VERIFY_SURFACE_URL } from "./continuityTraversalHandoff";

function trustSurfaceApiOrigin(): string {
  return new URL(VERIFY_SURFACE_URL).origin;
}

export const VERIFIED_CROSS_POST_LINE =
  "Canonical continuity lives in XO. External platforms receive representations only." as const;

export const CROSS_POST_STEPS = ["Signal", "Preserve", "Represent", "Inspect"] as const;
export const CANONICALLS_CROSS_POST_LINE =
  "The canonical record becomes inspectable. Representations remain linked to the canonical source." as const;

export { CANONICALLS_CANONICALIZE_FLOW as CANONICALLS_CROSS_POST_STEPS } from "./canonicallsTraversal";

export type CrossPostSurface = "xo" | "canonicalls";

export type CrossPostStepId = (typeof CROSS_POST_STEPS)[number];

export type CrossPostPhase = "compose" | "confirm" | "preserve" | "crosspost" | "verify";

export type CrossPostDraft = {
  title: string;
  body: string;
  author: string;
  aiAssisted: boolean;
  humanConfirmed: boolean;
};

export type CrossPostVariants = {
  twitter: string;
  linkedin: string;
  discord: string;
};

export type PreservedCrossPost = {
  postId: string;
  digest: string;
  artifactUrl: string;
  verifyUrl: string;
  publicContinuityUrl: string;
  replayUrl: string;
  preservedAt: string;
  draft: CrossPostDraft;
  ledgerEventCount?: number;
  mode: "signed" | "local";
};

const LOCAL_PREFIX = "xo-verified-cross-post:";

export function crossPostPhase(step: CrossPostStepId): CrossPostPhase {
  if (step === "Signal") return "compose";
  if (step === "Preserve") return "confirm";
  if (step === "Represent") return "crosspost";
  return "verify";
}

export function crossPostStepIndex(phase: CrossPostPhase): number {
  if (phase === "compose") return 0;
  if (phase === "confirm" || phase === "preserve") return 1;
  if (phase === "crosspost") return 2;
  return 3;
}

export function slugFromTitle(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const stamp = Date.now().toString(36).slice(-6);
  return `cross-${base || "post"}-${stamp}`;
}

/** Deterministic assist — shortens and clarifies without claiming external AI. */
export function suggestAssistedSummary(body: string, maxLen = 280): string {
  const text = body.trim().replace(/\s+/g, " ");
  if (!text) return "";
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  let out = sentences[0] ?? text;
  if (out.length < maxLen * 0.6 && sentences[1]) {
    out = `${out} ${sentences[1]}`.trim();
  }
  if (out.length > maxLen) {
    const cut = out.slice(0, maxLen - 1).trimEnd();
    const lastSpace = cut.lastIndexOf(" ");
    out = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
  }
  return out;
}

export function buildCrossPostVariants(
  draft: CrossPostDraft,
  publicUrl: string,
  surface: CrossPostSurface = "xo",
): CrossPostVariants {
  const title = draft.title.trim() || (surface === "canonicalls" ? "Canonical record" : "Continuity note");
  const body = draft.body.trim();
  const byline = draft.author.trim() || (surface === "canonicalls" ? "Continuity author" : "XO continuity author");
  const verify = publicUrl.trim();
  const excerpt = body.length > 160 ? `${body.slice(0, 157).trimEnd()}…` : body;

  if (surface === "canonicalls") {
    const twitter = [title, excerpt, "", `— ${byline}`, verify ? `Canonical source · ${verify}` : ""]
      .filter(Boolean)
      .join("\n")
      .slice(0, 280);

    const linkedin = [
      title,
      "",
      body,
      "",
      "Published as a representation. The canonical record remains inspectable separately.",
      verify ? `Canonical source: ${verify}` : "",
      byline,
    ]
      .filter(Boolean)
      .join("\n");

    const discord = [
      `**${title}**`,
      excerpt,
      "",
      "_Representation preview — canonical record is the source._",
      verify ? `Source · ${verify}` : "",
    ].join("\n");

    return { twitter, linkedin, discord };
  }

  const twitter = [
    title,
    body.length > 200 ? `${body.slice(0, 197)}…` : body,
    "",
    `Continuity record · ${byline}`,
    verify ? `Inspect: ${verify}` : "",
  ]
    .filter(Boolean)
    .join("\n")
    .slice(0, 280);

  const linkedin = [
    title,
    "",
    body,
    "",
    "This message is preserved as inspectable continuity in XO — not platform-native trust.",
    verify ? `Verify continuity: ${verify}` : "",
    `Author continuity: ${byline}`,
  ]
    .filter(Boolean)
    .join("\n");

  const discord = [
    `**${title}**`,
    body,
    "",
    "_Canonical continuity lives in XO. This channel carries a representation._",
    verify ? `Verify · ${verify}` : "",
  ].join("\n");

  return { twitter, linkedin, discord };
}

export function buildPublicContinuityUrl(
  postId: string,
  origin?: string,
  surface: CrossPostSurface = "xo",
): string {
  const base = (origin ?? (typeof window !== "undefined" ? window.location.origin : "https://xo.watch")).replace(
    /\/+$/,
    "",
  );
  const path = surface === "canonicalls" ? "/canonicalize" : "/cross-post";
  return `${base}${path}?post=${encodeURIComponent(postId)}`;
}

export function buildReplayUrl(
  postId: string,
  origin?: string,
  surface: CrossPostSurface = "xo",
): string {
  const base = (origin ?? (typeof window !== "undefined" ? window.location.origin : "https://xo.watch")).replace(
    /\/+$/,
    "",
  );
  if (surface === "canonicalls") {
    return `${base}/replay?cross_post=${encodeURIComponent(postId)}`;
  }
  return `${base}/continuity-replay?cross_post=${encodeURIComponent(postId)}`;
}

export function buildVerifyUrlForDigest(digest: string): string {
  const url = new URL(VERIFY_SURFACE_URL);
  url.searchParams.set("traverse", "1");
  url.searchParams.set("sha", digest.trim().toLowerCase());
  url.searchParams.set("source", "cross-post");
  return url.href;
}

export function canonicalBodyForRecord(draft: CrossPostDraft): string {
  const meta = [
    "---",
    "xo_continuity:",
    `  author: ${draft.author.trim() || "unknown"}`,
    `  ai_assisted: ${draft.aiAssisted ? "true" : "false"}`,
    `  human_confirmed: ${draft.humanConfirmed ? "true" : "false"}`,
    "  representation_only_external: true",
  ].join("\n");
  return `${draft.body.trim()}\n\n${meta}`.trim();
}

export async function preserveCrossPostOnTrustSurface(
  draft: CrossPostDraft,
  postId: string,
): Promise<PreservedCrossPost> {
  const api = trustSurfaceApiOrigin();
  const body = canonicalBodyForRecord(draft);
  const res = await fetch(`${api}/api/sign-post`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      slug: postId,
      title: draft.title.trim() || "Continuity cross-post",
      body,
      lang: "en",
    }),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || json.ok === false) {
    throw new Error(String(json.detail ?? json.error ?? `sign-post failed (${res.status})`));
  }
  const digest = String(json.digest ?? "").trim().toLowerCase();
  const artifactUrl = String(json.artifact_url ?? "").trim();
  const verifyUrl = String(json.verify_url ?? "").trim() || (digest ? buildVerifyUrlForDigest(digest) : "");
  const preserved: PreservedCrossPost = {
    postId: String(json.post_id ?? postId),
    digest,
    artifactUrl,
    verifyUrl,
    publicContinuityUrl: buildPublicContinuityUrl(postId),
    replayUrl: buildReplayUrl(postId),
    preservedAt: new Date().toISOString(),
    draft: { ...draft, humanConfirmed: true },
    mode: "signed",
  };
  try {
    const ledgerRes = await fetch(`${api}/api/ledger/post/${encodeURIComponent(preserved.postId)}`, {
      headers: { Accept: "application/json" },
    });
    if (ledgerRes.ok) {
      const ledger = (await ledgerRes.json()) as { events?: unknown[] };
      if (Array.isArray(ledger.events)) {
        preserved.ledgerEventCount = ledger.events.length;
      }
    }
  } catch {
    /* ledger trace is best-effort */
  }
  saveLocalCrossPost(preserved);
  return preserved;
}

export function preserveCrossPostLocal(draft: CrossPostDraft, postId: string): PreservedCrossPost {
  const digest = localDigestPlaceholder(postId, draft);
  const preserved: PreservedCrossPost = {
    postId,
    digest,
    artifactUrl: "",
    verifyUrl: "",
    publicContinuityUrl: buildPublicContinuityUrl(postId),
    replayUrl: buildReplayUrl(postId),
    preservedAt: new Date().toISOString(),
    draft: { ...draft, humanConfirmed: true },
    ledgerEventCount: 0,
    mode: "local",
  };
  saveLocalCrossPost(preserved);
  return preserved;
}

function localDigestPlaceholder(postId: string, draft: CrossPostDraft): string {
  const raw = `${postId}:${draft.title}:${draft.body}:${draft.author}`;
  let h = 0;
  for (let i = 0; i < raw.length; i++) h = (Math.imul(31, h) + raw.charCodeAt(i)) | 0;
  const hex = Math.abs(h).toString(16).padStart(8, "0");
  return `${hex.repeat(8)}`.slice(0, 64);
}

export function saveLocalCrossPost(record: PreservedCrossPost): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(`${LOCAL_PREFIX}${record.postId}`, JSON.stringify(record));
  } catch {
    /* ignore */
  }
}

export function loadLocalCrossPost(postId: string): PreservedCrossPost | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${LOCAL_PREFIX}${postId}`);
    if (!raw) return null;
    return JSON.parse(raw) as PreservedCrossPost;
  } catch {
    return null;
  }
}

/** Most recently preserved local cross-post (visitor replay → call handoff). */
export function loadLatestLocalCrossPost(): PreservedCrossPost | null {
  if (typeof localStorage === "undefined") return null;
  let best: PreservedCrossPost | null = null;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(LOCAL_PREFIX)) continue;
      const postId = key.slice(LOCAL_PREFIX.length);
      const record = loadLocalCrossPost(postId);
      if (!record) continue;
      if (!best || record.preservedAt.localeCompare(best.preservedAt) > 0) best = record;
    }
  } catch {
    return best;
  }
  return best;
}
